import { I as memo, J as forwardRef, K as createElement, L as createHook, N as usePopoverContext, O as invariant, P as useStoreState, Q as getBasePlacement, R as useId, S as useMergeRefs, k as jsxRuntimeExports, U as removeUndefinedValues, V as useSafeLayoutEffect, W as getWindow, X as usePopoverStore, l as DocContext, Y as PopoverDisclosure, Z as MdInfoOutline, $ as Popover } from "./doenetml-drNYjJiF.js";
import { useMemo, useState, useRef, useContext } from "react";
const POPOVER_ARROW_PATH = "M23 27.8C24.1 29 26.4 30 28 30H30H0H2C3.7 30 5.9 29 7 27.8L14 20.6C14.7 19.8 15.3 19.8 16 20.6L23 27.8Z";
const TagName = "div";
const defaultSize = 30;
const halfDefaultSize = defaultSize / 2;
const rotateMap = {
  top: `rotate(180 ${halfDefaultSize} ${halfDefaultSize})`,
  right: `rotate(-90 ${halfDefaultSize} ${halfDefaultSize})`,
  bottom: `rotate(0 ${halfDefaultSize} ${halfDefaultSize})`,
  left: `rotate(90 ${halfDefaultSize} ${halfDefaultSize})`
};
function useComputedStyle(store) {
  const [style, setStyle] = useState();
  const contentElement = useStoreState(store, "contentElement");
  useSafeLayoutEffect(() => {
    if (!contentElement) return;
    setStyle(getWindow(contentElement).getComputedStyle(contentElement));
  }, [contentElement]);
  return style;
}
function maskParentheses(text) {
  let masked = "";
  let depth = 0;
  for (const char of text) {
    if (char === "(") {
      depth += 1;
      masked += char;
      continue;
    }
    if (char === ")") {
      depth = Math.max(0, depth - 1);
      masked += char;
      continue;
    }
    masked += depth > 0 ? " " : char;
  }
  return masked;
}
const ringLengthsRegex = /(?:^|\s)0(?:px)?\s+0(?:px)?\s+0(?:px)?\s+((?:\d*\.)?\d+)px(?=\s|$)/;
function getRingFromSegment(segment, maskedSegment) {
  const match = maskedSegment.match(ringLengthsRegex);
  if (!match) return;
  const spread = match[1];
  if (!spread) return;
  const width = Number.parseFloat(spread);
  if (!width) return;
  const lengthsStart = match.index ?? 0;
  const lengthsEnd = lengthsStart + match[0].length;
  return {
    width,
    color: `${segment.slice(0, lengthsStart)} ${segment.slice(lengthsEnd)}`.replace(/\binset\b/g, " ").trim() || void 0
  };
}
function getRing(style) {
  if (!style) return;
  const boxShadow = style.getPropertyValue("box-shadow");
  if (!boxShadow) return;
  if (boxShadow === "none") return;
  const masked = maskParentheses(boxShadow);
  let segmentStart = 0;
  for (let index = 0; index <= masked.length; index += 1) {
    if (index !== masked.length && masked[index] !== ",") continue;
    const ring = getRingFromSegment(boxShadow.slice(segmentStart, index), masked.slice(segmentStart, index));
    if (ring) return ring;
    segmentStart = index + 1;
  }
}
const usePopoverArrow = createHook(function usePopoverArrow2({ store, size = defaultSize, borderWidth: borderWidthProp, ...props }) {
  const context = usePopoverContext();
  store = store || context;
  invariant(store, process.env.NODE_ENV !== "production" && "PopoverArrow must be wrapped in a Popover component.");
  const dir = useStoreState(store, (state) => getBasePlacement(state.currentPlacement));
  const maskId = useId();
  const style = useComputedStyle(store);
  const fill = style?.getPropertyValue("background-color") || "none";
  const [borderWidth, isRing, ringColor] = useMemo(() => {
    if (borderWidthProp != null) return [
      borderWidthProp,
      false,
      void 0
    ];
    if (!style) return [
      0,
      false,
      void 0
    ];
    const ring = getRing(style);
    if (ring) return [
      Math.ceil(ring.width),
      true,
      ring.color
    ];
    const borderWidth2 = style.getPropertyValue(`border-${dir}-width`);
    if (borderWidth2) {
      const parsed = Number.parseFloat(borderWidth2);
      if (!Number.isNaN(parsed)) return [
        Math.ceil(parsed),
        false,
        void 0
      ];
    }
    return [
      0,
      false,
      void 0
    ];
  }, [
    borderWidthProp,
    style,
    dir
  ]);
  const fallbackColor = isRing ? style?.getPropertyValue("color") : style?.getPropertyValue(`border-${dir}-color`);
  const stroke = ringColor || fallbackColor || "none";
  const strokeWidth = borderWidth * 2 * (defaultSize / size);
  const transform = rotateMap[dir];
  props = {
    children: useMemo(() => /* @__PURE__ */ jsxRuntimeExports.jsx("svg", {
      display: "block",
      viewBox: "0 0 30 30",
      children: /* @__PURE__ */ jsxRuntimeExports.jsxs("g", {
        transform,
        children: [
          !isRing && /* @__PURE__ */ jsxRuntimeExports.jsx("path", {
            fill: "none",
            stroke: fill,
            d: "M23 27.8C24.1 29 26.4 30 28 30H30H0H2C3.7 30 5.9 29 7 27.8L14 20.6C14.7 19.8 15.3 19.8 16 20.6L23 27.8Z",
            mask: `url(#${maskId})`
          }),
          /* @__PURE__ */ jsxRuntimeExports.jsx("path", {
            fill: "none",
            d: POPOVER_ARROW_PATH,
            mask: `url(#${maskId})`
          }),
          /* @__PURE__ */ jsxRuntimeExports.jsx("path", {
            stroke: "none",
            d: POPOVER_ARROW_PATH
          }),
          /* @__PURE__ */ jsxRuntimeExports.jsx("mask", {
            id: maskId,
            maskUnits: "userSpaceOnUse",
            children: /* @__PURE__ */ jsxRuntimeExports.jsx("rect", {
              x: "-15",
              y: "0",
              width: "60",
              height: "30",
              fill: "white",
              stroke: "black"
            })
          })
        ]
      })
    }), [
      transform,
      isRing,
      fill,
      maskId
    ]),
    "aria-hidden": true,
    ...props,
    ref: useMergeRefs(store.setArrowElement, props.ref),
    style: {
      position: "absolute",
      fontSize: size,
      width: "1em",
      height: "1em",
      pointerEvents: "none",
      fill,
      stroke,
      strokeWidth,
      ...props.style
    }
  };
  return removeUndefinedValues(props);
});
const PopoverArrow = memo(forwardRef(function PopoverArrow2(props) {
  return createElement(TagName, usePopoverArrow(props));
}));
function DescriptionAsDetails({ children }) {
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("details", { className: "description", "data-test": "Description", children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx("summary", { title: "more information", "data-test": "Description Summary", children: /* @__PURE__ */ jsxRuntimeExports.jsx(MdInfoOutline, {}) }),
    /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "details-content", children })
  ] });
}
function DescriptionPopover({ children }) {
  const popover = usePopoverStore();
  const popoverRef = useRef(null);
  const { darkMode } = useContext(DocContext) || {};
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx(
      PopoverDisclosure,
      {
        store: popover,
        className: "description-popover-button",
        title: "more information",
        "data-test": "Description Button",
        children: /* @__PURE__ */ jsxRuntimeExports.jsx(MdInfoOutline, {})
      }
    ),
    /* @__PURE__ */ jsxRuntimeExports.jsxs(
      Popover,
      {
        store: popover,
        ref: popoverRef,
        initialFocus: popoverRef,
        gutter: 2,
        "data-theme": darkMode ?? "light",
        className: "description-popover",
        "data-test": "Description",
        children: [
          /* @__PURE__ */ jsxRuntimeExports.jsx(PopoverArrow, {}),
          children
        ]
      }
    )
  ] });
}
export {
  DescriptionPopover as D,
  PopoverArrow as P,
  DescriptionAsDetails as a
};
